import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { FauxDayHours, FauxMerchant, MiddlewareApi } from '../../core/services/middleware-api.service';

/**
 * THE FAUX MERCHANTS — a hundred synthetic ohio liquor stores (funny names, real Ohio addresses)
 * that live in the middleware only, for development and stress testing.
 *
 * The list is the truth; "Create in Magento" is the explicit, idempotent materialisation step: the
 * real provisioning (website, store view, MSI source + stock, marked is_synthetic), the location
 * registration, and the hours push. Once created, the row shows the Magento resources it received.
 * "Create all" walks every uncreated merchant sequentially — provisioning is heavy Magento work,
 * and one at a time is what keeps a hundred of them a stress test instead of an outage.
 */
@Component({
    selector: 'app-faux-merchants',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, DialogModule, InputTextModule, ToggleSwitchModule, TooltipModule, FormsModule],
    template: `
        <p-card>
            <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <h5 class="mb-1">Faux merchants<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Test stores. Create in Magento sets up the real website and store for each one." tooltipPosition="top"></i></h5>
                </div>
                <div class="flex gap-2">
                    @if (merchants().length < 100) {
                        <p-button icon="pi pi-sparkles" label="Generate roster" size="small" severity="secondary"
                                  [loading]="generating()" (onClick)="generate()" />
                    }
                    <p-button icon="pi pi-cloud-upload" [label]="createAllLabel()" size="small" severity="secondary"
                              [loading]="creatingAll()" [disabled]="!uncreatedCount()" (onClick)="createAll()" />
                </div>
            </div>

            @if (note()) {
                <p class="text-sm text-surface-500 mb-3">{{ note() }}</p>
            }

            <p-table [value]="merchants()" styleClass="p-datatable-sm" [paginator]="true" [rows]="25">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Name</th><th>Address</th><th>Hours</th><th>Magento</th><th style="width:14rem"></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-m>
                    <tr>
                        <td>
                            <span class="font-medium">{{ m.name }}</span>
                            <span class="block text-xs text-surface-500">{{ m.locationId }} · {{ m.merchantCode }}</span>
                        </td>
                        <td class="text-sm">
                            {{ m.street }}<span class="text-surface-500">, {{ m.city }} · {{ m.county }} Co. {{ m.postcode }}</span>
                        </td>
                        <td class="text-sm whitespace-nowrap">{{ hoursSummary(m) }}</td>
                        <td>
                            <div class="flex gap-1 flex-wrap">
                                @if (m.createdInMagento) {
                                    <p-tag severity="success" [value]="'Created · site ' + m.magentoWebsiteId" />
                                    @if (m.catalogCopiedAt) {
                                        <p-tag severity="info" value="Catalog" />
                                    }
                                } @else {
                                    <p-tag severity="secondary" value="Not created" />
                                }
                            </div>
                        </td>
                        <td>
                            <div class="flex gap-2 justify-end">
                                <p-button [label]="m.createdInMagento ? 'Update in Magento' : 'Create in Magento'"
                                          size="small" [severity]="m.createdInMagento ? 'secondary' : 'primary'"
                                          [loading]="creatingId() === m.id" (onClick)="createOne(m)" />
                                @if (m.createdInMagento) {
                                    <p-button [label]="m.catalogCopiedAt ? 'Refresh catalog' : 'Copy catalog'"
                                              size="small" severity="secondary"
                                              [loading]="copyingId() === m.id" (onClick)="copyCatalog(m)" />
                                }
                                <p-button label="Hours" size="small" [text]="true" (onClick)="openHours(m)" />
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>

        <!-- The hours editor: both schedules side by side. Saving pushes straight into Magento's
             business-hours config when the merchant is already materialised. -->
        <p-dialog [(visible)]="hoursOpen" [modal]="true" [style]="{ width: '46rem' }"
                  [header]="'Hours — ' + (hoursTarget()?.name ?? '')">
            @if (hoursTarget(); as t) {
                <div class="grid grid-cols-2 gap-6">
                    @for (section of [{ label: 'Delivery', hours: editDelivery }, { label: 'Pickup', hours: editPickup }]; track section.label) {
                        <div>
                            <h6 class="mb-2">{{ section.label }}</h6>
                            @for (day of days; track day) {
                                <div class="flex items-center gap-2 mb-2">
                                    <p-toggleswitch [(ngModel)]="section.hours[day].enabled" />
                                    <span class="w-20 text-sm capitalize">{{ day }}</span>
                                    <input pInputText class="w-20 text-sm" [(ngModel)]="section.hours[day].open"
                                           [disabled]="!section.hours[day].enabled" placeholder="09:00" />
                                    <span class="text-surface-500">–</span>
                                    <input pInputText class="w-20 text-sm" [(ngModel)]="section.hours[day].close"
                                           [disabled]="!section.hours[day].enabled" placeholder="21:00" />
                                </div>
                            }
                        </div>
                    }
                </div>
                <div class="flex items-center justify-between mt-4">
                    <span class="text-sm text-surface-500">
                        {{ t.createdInMagento ? 'Saving pushes these hours into Magento.' : 'Saved here; they push when the merchant is created in Magento.' }}
                    </span>
                    <p-button label="Save hours" size="small" [loading]="savingHours()" (onClick)="saveHours()" />
                </div>
            }
        </p-dialog>
    `
})
export class FauxMerchants implements OnInit {
    private readonly api = inject(MiddlewareApi);

    readonly days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    readonly merchants = signal<FauxMerchant[]>([]);
    readonly generating = signal(false);
    readonly creatingId = signal<string | null>(null);
    readonly copyingId = signal<string | null>(null);
    readonly creatingAll = signal(false);
    readonly createAllProgress = signal<{ done: number; total: number } | null>(null);
    readonly note = signal<string | null>(null);
    readonly savingHours = signal(false);
    readonly hoursTarget = signal<FauxMerchant | null>(null);

    hoursOpen = false;
    editDelivery: Record<string, FauxDayHours> = {};
    editPickup: Record<string, FauxDayHours> = {};

    ngOnInit(): void {
        this.reload();
    }

    uncreatedCount(): number {
        return this.merchants().filter((m) => !m.createdInMagento).length;
    }

    createAllLabel(): string {
        const p = this.createAllProgress();
        if (p) return `Creating ${p.done}/${p.total}…`;
        const n = this.uncreatedCount();
        return n ? `Create all in Magento (${n})` : 'All created';
    }

    hoursSummary(m: FauxMerchant): string {
        const mon = m.deliveryHours?.['monday'];
        const sun = m.deliveryHours?.['sunday'];
        if (!mon) return '—';
        const base = `Mon ${mon.open}–${mon.close}`;
        return sun?.enabled ? `${base} · Sun open` : `${base} · Sun closed`;
    }

    reload(): void {
        this.api.getFauxMerchants().subscribe({
            next: (rows) => this.merchants.set(rows),
            error: (e) => this.note.set(e?.error?.detail || e?.message || 'The roster could not be loaded.')
        });
    }

    generate(): void {
        this.generating.set(true);
        this.api.generateFauxMerchants().subscribe({
            next: (r) => {
                this.generating.set(false);
                this.note.set(r.created ? `Generated ${r.created} merchant(s) — roster at ${r.total}.` : 'Roster already complete.');
                this.reload();
            },
            error: (e) => {
                this.generating.set(false);
                this.note.set(e?.error?.detail || e?.message || 'Generation failed.');
            }
        });
    }

    createOne(m: FauxMerchant): void {
        this.creatingId.set(m.id);
        this.api.createFauxMerchantInMagento(m.id).subscribe({
            next: (r) => {
                this.creatingId.set(null);
                this.patch(r.merchant);
                this.note.set(`${r.merchant.name} ${r.createdNow ? 'created' : 'updated'} — website ${r.merchant.magentoWebsiteId}, `
                    + `location ${r.registeredLocation ? 'registered' : 'NOT registered'}, `
                    + `hours ${r.hoursPushed ? 'pushed' : 'pending'}.`);
            },
            error: (e) => {
                this.creatingId.set(null);
                this.note.set(`${m.name}: ${e?.error?.detail || e?.message || 'creation failed.'}`);
            }
        });
    }

    /// Sequential on purpose: each creation is heavy Magento work (website + store + reinit). One
    /// at a time keeps a hundred of them a stress test, not an outage — and every step is
    /// idempotent, so an interrupted walk resumes exactly where it stopped.
    async createAll(): Promise<void> {
        const targets = this.merchants().filter((m) => !m.createdInMagento);
        if (!targets.length) return;
        this.creatingAll.set(true);
        let done = 0;
        const failures: string[] = [];
        for (const m of targets) {
            this.createAllProgress.set({ done, total: targets.length });
            try {
                const r = await new Promise<{ merchant: FauxMerchant }>((resolve, reject) =>
                    this.api.createFauxMerchantInMagento(m.id).subscribe({ next: resolve, error: reject }));
                this.patch(r.merchant);
            } catch (e: unknown) {
                failures.push(m.name);
            }
            done++;
        }
        this.creatingAll.set(false);
        this.createAllProgress.set(null);
        this.note.set(failures.length
            ? `Created ${done - failures.length}/${targets.length}; failed: ${failures.slice(0, 5).join(', ')}${failures.length > 5 ? '…' : ''} — pressing again retries only those.`
            : `All ${targets.length} merchant(s) created in Magento.`);
    }

    /// The fast fill: seconds, not hours — every sellable ohio item becomes the store's OWN
    /// product ({location-id}-{ohio} sku) at ohio prices, stock unmanaged.
    copyCatalog(m: FauxMerchant): void {
        this.copyingId.set(m.id);
        this.api.copyFauxMerchantCatalog(m.id).subscribe({
            next: (r) => {
                this.copyingId.set(null);
                this.patch(r.merchant);
                const c = r.copy as { productsCopied?: number; rewritesCopied?: number } | null;
                this.note.set(`${r.merchant.name}: catalog copied — ${c?.productsCopied ?? '?'} products, `
                    + `${c?.rewritesCopied ?? '?'} URL rewrites. Indexers refresh on the next cron pass.`);
            },
            error: (e) => {
                this.copyingId.set(null);
                this.note.set(`${m.name}: ${e?.error?.detail || e?.error?.message || e?.message || 'the copy failed.'}`);
            }
        });
    }

    openHours(m: FauxMerchant): void {
        this.hoursTarget.set(m);
        this.editDelivery = this.cloneHours(m.deliveryHours);
        this.editPickup = this.cloneHours(m.pickupHours);
        this.hoursOpen = true;
    }

    saveHours(): void {
        const t = this.hoursTarget();
        if (!t) return;
        this.savingHours.set(true);
        this.api.updateFauxMerchantHours(t.id, { deliveryHours: this.editDelivery, pickupHours: this.editPickup }).subscribe({
            next: (r) => {
                this.savingHours.set(false);
                this.hoursOpen = false;
                this.patch(r.merchant);
                this.note.set(`${r.merchant.name}: hours saved${t.createdInMagento ? (r.hoursPushed ? ' and pushed to Magento.' : ' — the Magento push failed; saving again retries.') : '.'}`);
            },
            error: (e) => {
                this.savingHours.set(false);
                this.note.set(e?.error?.detail || e?.message || 'The hours could not be saved.');
            }
        });
    }

    private cloneHours(hours: Record<string, FauxDayHours> | null): Record<string, FauxDayHours> {
        const out: Record<string, FauxDayHours> = {};
        for (const d of this.days) {
            const h = hours?.[d];
            out[d] = { enabled: h?.enabled ?? true, open: h?.open ?? '09:00', close: h?.close ?? '21:00' };
        }
        return out;
    }

    private patch(updated: FauxMerchant): void {
        this.merchants.set(this.merchants().map((m) => (m.id === updated.id ? updated : m)));
    }
}
